import type { ReactNode, Ref } from "react";
import { Panel } from "@/components/primitives";

/**
 * Pod 상세 **세션 패널 껍데기** (ADR 0021 · ADR 0022)
 * --------------------------------------------------------------------------
 * Live Logs와 Debug Terminal은 서로 다른 전송을 쓰지만 껍데기는 같습니다 —
 * 열기 전에는 아무것도 요청하지 않고, 열면 진입 정보를 한 번 받고, 실패하면 서버
 * 문구를 그대로 보여줍니다. 같은 껍데기를 두 벌 두면 "열기 전 무연결" 같은 규칙을
 * 한쪽만 고치는 일이 생기므로 한 곳에 모읍니다.
 */

/** 패널 안내 배너. tone·inset·role만 다르고 마크업은 같습니다. */
export function Notice({
  tone,
  inset,
  role,
  testId,
  children,
}: {
  tone?: "warning";
  /** 패널 전체 여백. 컨트롤 위에 단독으로 뜰 때 씁니다. */
  inset?: boolean;
  role?: "alert" | "status" | "note";
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={tone ? "notice notice--warning" : "notice"}
      style={{ margin: inset ? "var(--space-5)" : "0 var(--space-5) var(--space-5)" }}
      role={role}
      data-testid={testId}
    >
      <span aria-hidden="true">{tone ? "!" : "·"}</span>
      <span>{children}</span>
    </div>
  );
}

export function SessionPanel({
  title,
  subtitle,
  actions,
  open,
  onOpen,
  openLabel,
  openTestId,
  openRef,
  openHint,
  loading,
  loadingLabel,
  error,
  errorTestId,
  footer,
  children,
}: {
  title: string;
  subtitle: string;
  actions?: ReactNode;
  open: boolean;
  onOpen: () => void;
  openLabel: string;
  /** 열기 버튼의 안정적인 선택자. 라벨은 표시 언어를 따르므로 E2E가 이름으로 찾을 수 없습니다. (ADR 0025) */
  openTestId?: string;
  /** 열기 버튼 참조. 패널을 닫는 쪽이 초점을 되돌릴 수 있어야 합니다(키보드 탈출구). */
  openRef?: Ref<HTMLButtonElement>;
  /** 열기 전 안내. 연결이 언제 생기는지 사용자가 알아야 합니다. */
  openHint?: ReactNode;
  loading: boolean;
  loadingLabel: string;
  /** 서버가 준 문구. 화면이 사유를 지어내지 않습니다. */
  error?: ReactNode;
  errorTestId?: string;
  /** 열려 있으면 오류 여부와 무관하게 항상 보이는 영역(닫기 등). */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Panel title={title} subtitle={subtitle} actions={actions} flush>
      {!open ? (
        <div className="pad-5" style={{ paddingTop: "var(--space-5)" }}>
          <button ref={openRef} type="button" className="chip" data-testid={openTestId} onClick={onOpen}>
            {openLabel}
          </button>
          {openHint ? <p className="muted" style={{ marginTop: "var(--space-4)" }}>{openHint}</p> : null}
        </div>
      ) : (
        <>
          {loading ? <p className="muted" style={{ padding: "var(--space-5)" }}>{loadingLabel}</p> : null}
          {error ? (
            <Notice tone="warning" inset role="alert" testId={errorTestId}>
              {error}
            </Notice>
          ) : null}
          {!loading && !error ? children : null}
          {footer}
        </>
      )}
    </Panel>
  );
}
